import React, { useEffect, useState } from 'react';
import { GridApiPro, GridColDef } from '@mui/x-data-grid-pro';
import { PageMetadata } from '../components/datagridComponents/paginationHelpers';
import { ColumnGroup } from '../components/datagridComponents/GridColumns';

const DataGridConfigContext = React.createContext({
  data: undefined,
  fetchData: () => {
    /* */
  },
  metadata: undefined as PageMetadata,
  isLoading: false,
  isError: false,
  fetchedFirstData: false,
  gridApiRef: undefined as React.MutableRefObject<GridApiPro>,
  setGridApiRef: (_gridApiRef: React.MutableRefObject<GridApiPro>) => {
    /* */
  },
  translationKey: '',
  addButtonNavigationPath: '0',
  endpoint: '',
  cacheKey: '',
  sentryMsg: '',
  successMessage: '',
  setSuccessMessage: (_msg: string) => {
    /* */
  },
  errorMessage: '',
  setErrorMessage: (_msg: string) => {
    /* */
  },
  errors: {},
  setErrors: (_errors) => {
    /* */
  },
  MobileRowMainContent: undefined as React.ComponentType<{ data: unknown }>,
  MobileRowExpandableContent: undefined as React.ComponentType<{
    data: unknown;
  }>,
  hasMobileExpandableContent: true,
  BuildColumns: () => [] as GridColDef[],
  CreateComponent: undefined as React.ComponentType<{
    currentObject: boolean | unknown;
    resetObject: () => void;
    setSuccessMessage: (message: string) => void;
    errorMessage: string;
    setErrorMessage: (message: string) => void;
    errors: unknown;
    setErrors: (obj: unknown) => void;
    mode: string;
  }>,
  showColumnsButton: false,
  columnVisibilityModel: {} as Record<string, boolean>,
  changeColumnVisibility: (_columnName: string, _visible: boolean) => {
    /* */
  },
  columnGroups: undefined as ColumnGroup[],
});

const DataGridConfigContextProvider = <T, TErrors>({
  data,
  fetchData,
  metadata,
  isLoading,
  isError,
  translationKey,
  endpoint,
  cacheKey,
  sentryMsg,
  addButtonNavigationPath = '0',
  MobileRowMainContent,
  MobileRowExpandableContent,
  BuildColumns,
  CreateComponent,
  columnVisibility,
  columnGroups,
  children,
}: {
  data: T[];
  fetchData: () => unknown;
  metadata: PageMetadata;
  isLoading: boolean;
  isError: boolean;
  translationKey: string;
  endpoint: string;
  cacheKey: string;
  sentryMsg: string;
  addButtonNavigationPath?: string;
  MobileRowMainContent: React.ComponentType<{ data: T }>;
  MobileRowExpandableContent?: React.ComponentType<{
    data: T;
  }>;
  BuildColumns: () => GridColDef[];
  CreateComponent: React.ComponentType<{
    currentObject: boolean | T;
    resetObject: () => void;
    setSuccessMessage: (message: string) => void;
    errorMessage: string;
    setErrorMessage: (message: string) => void;
    errors: TErrors;
    setErrors: (obj: TErrors) => void;
    mode: string;
  }>;
  columnVisibility?: Record<string, boolean>;
  columnGroups?: ColumnGroup[];
  children: JSX.Element;
}) => {
  const [gridApiRef, setGridApiRef] = useState<React.MutableRefObject<GridApiPro>>(undefined);
  const [fetchedFirstData, setFetchedFirstData] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [errors, setErrors] = useState<TErrors>({} as TErrors);
  const [columnsVisibility, setColumnsVisibility] = useState<Record<string, boolean>>(
    columnVisibility || {}
  );

  const changeColumnVisibility = (columnName: string, visible: boolean) => {
    if (columnName in columnsVisibility) {
      setColumnsVisibility({
        ...columnsVisibility,
        [columnName]: visible,
      });
    }
  };

  useEffect(() => {
    if (data) setFetchedFirstData(true);
  }, [data]);

  const contextValue = {
    data,
    fetchData,
    metadata,
    fetchedFirstData,
    gridApiRef,
    setGridApiRef,
    translationKey,
    addButtonNavigationPath,
    endpoint,
    cacheKey,
    sentryMsg,
    isLoading,
    isError,
    successMessage,
    setSuccessMessage,
    errorMessage,
    setErrorMessage,
    errors,
    setErrors,
    MobileRowMainContent,
    MobileRowExpandableContent,
    hasMobileExpandableContent: Boolean(MobileRowExpandableContent),
    BuildColumns,
    CreateComponent,
    showColumnsButton: Object.keys(columnsVisibility)?.length > 0,
    columnVisibilityModel: columnsVisibility,
    changeColumnVisibility,
    columnGroups,
  };

  return (
    <DataGridConfigContext.Provider value={contextValue}>{children}</DataGridConfigContext.Provider>
  );
};

export { DataGridConfigContext, DataGridConfigContextProvider };
